import React, { useState, useEffect } from 'react';

// Import scroll image assets
import scrollTop from '../assets/scroll/scroll-top.webp';
import scrollMiddle from '../assets/scroll/scroll-middle.webp';
import scrollBottom from '../assets/scroll/scroll-bottom.webp';

/**
 * ScrollPanel - An animated unfurling scroll using real image assets
 * Starts rolled up with magical glow and animates open
 */
const ScrollPanel = ({
    children,
    title = "Choose Your Journey",
    headerContent,
    disabled = false,
    onExpanded
}) => {
    const [phase, setPhase] = useState('collapsed'); // 'collapsed' | 'unfurling' | 'expanded'
    const [parchmentHeight, setParchmentHeight] = useState(0);
    const [showContent, setShowContent] = useState(false);

    // Count renderable menu items, including children nested inside fragments.
    const countRenderableChildren = (nodes) => {
        let count = 0;
        React.Children.forEach(nodes, (child) => {
            if (child === null || child === undefined || typeof child === 'boolean') return;
            if (React.isValidElement(child) && child.type === React.Fragment) {
                count += countRenderableChildren(child.props.children);
                return;
            }
            count += 1;
        });
        return count;
    };

    // Calculate target height based on number of menu items
    const childCount = Math.max(1, countRenderableChildren(children));
    const headerHeight = headerContent ? 44 : 0;
    const itemHeight = 46;
    const gap = 14;
    const padding = 48;
    const calculatedHeight = padding + headerHeight + (childCount * itemHeight) + ((childCount - 1) * gap);
    const minHeight = Math.max(220, childCount * 52);
    const targetHeight = Math.max(minHeight, calculatedHeight);

    useEffect(() => {
        if (disabled) return;
        const startTimer = setTimeout(() => setPhase('unfurling'), 700);
        return () => clearTimeout(startTimer);
    }, [disabled]);

    useEffect(() => {
        if (phase === 'unfurling') {
            const duration = 1600;
            let startTime = null;
            let animationId = null;
            let fallbackTimer = null;

            // Safari/background tab guard: ensure content is revealed even if RAF stalls.
            fallbackTimer = setTimeout(() => {
                setParchmentHeight(targetHeight);
                setPhase('expanded');
                setShowContent(true);
                onExpanded?.();
            }, duration + 350);

            const animate = (currentTime) => {
                if (!startTime) startTime = currentTime;
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                const eased = 1 - Math.pow(1 - progress, 3);
                setParchmentHeight(eased * targetHeight);

                if (progress < 1) {
                    animationId = requestAnimationFrame(animate);
                } else {
                    if (fallbackTimer) clearTimeout(fallbackTimer);
                    setPhase('expanded');
                    setTimeout(() => {
                        setShowContent(true);
                        onExpanded?.();
                    }, 50);
                }
            };
            animationId = requestAnimationFrame(animate);

            return () => {
                if (animationId) cancelAnimationFrame(animationId);
                if (fallbackTimer) clearTimeout(fallbackTimer);
            };
        }
    }, [phase, onExpanded, targetHeight]);

    // Update height when children change after expansion (e.g., Storyteller button appearing)
    useEffect(() => {
        if (phase === 'expanded') {
            setParchmentHeight(targetHeight);
        }
    }, [phase, targetHeight]);

    const isCollapsed = phase === 'collapsed';

    // Image dimensions (based on 500px source width)
    const scrollWidth = 420; // Display width
    const scale = scrollWidth / 500; // Scale factor from source
    const bottomHeight = Math.round(85 * scale); // 85px source -> ~71px

    return (
        <div className="relative flex flex-col items-center justify-center min-h-[500px]">
            {/* Title above scroll */}
            <h2
                className="text-2xl md:text-3xl text-amber-100 mb-4 transition-opacity duration-500"
                style={{
                    fontFamily: 'var(--ft-font-display)',
                    fontWeight: 600,
                    textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 0 20px rgba(218, 165, 32, 0.4)',
                    opacity: isCollapsed ? 1 : 0.8,
                }}
            >
                {title}
            </h2>

            {/* Magical glow behind collapsed scroll */}
            {isCollapsed && (
                <div
                    className="absolute animate-pulse-glow"
                    style={{
                        width: '450px',
                        height: '120px',
                        background: 'radial-gradient(ellipse, rgba(218, 165, 32, 0.4) 0%, rgba(218, 165, 32, 0.2) 40%, transparent 70%)',
                        filter: 'blur(20px)',
                        zIndex: 0,
                    }}
                />
            )}

            {/* The scroll assembly */}
            <div
                className={`relative transition-all duration-500 ${isCollapsed ? 'animate-float' : ''}`}
                style={{
                    filter: `drop-shadow(0 ${isCollapsed ? '0' : '12'}px ${isCollapsed ? '30' : '40'}px rgba(0, 0, 0, ${isCollapsed ? '0.4' : '0.6'}))`,
                    zIndex: 1,
                    opacity: 0.95,
                    width: `${scrollWidth}px`,
                }}
            >
                {/* Top roll - always visible */}
                <div
                    className="relative z-30 mx-auto"
                    style={{
                        width: `${scrollWidth}px`,
                    }}
                >
                    <img
                        src={scrollTop}
                        alt=""
                        className="w-full h-auto"
                        style={{ display: 'block' }}
                    />
                </div>

                {/* Parchment body - unfurls from top roll */}
                <div
                    className="relative z-20 mx-auto overflow-hidden"
                    style={{
                        width: `${scrollWidth}px`,
                        height: `${parchmentHeight}px`,
                        marginTop: '-10px',
                        transition: phase === 'collapsed' ? 'none' : undefined,
                    }}
                >
                    {/* Middle parchment image - stretched to fill */}
                    <img
                        src={scrollMiddle}
                        alt=""
                        className="absolute inset-0 w-full h-full"
                        style={{
                            objectFit: 'fill',
                        }}
                    />

                    {/* Content */}
                    <div
                        className="relative z-10 flex flex-col items-center justify-center h-full py-5 px-10 transition-opacity duration-500"
                        style={{
                            opacity: showContent ? 1 : 0,
                            transform: showContent ? 'translateY(0)' : 'translateY(-10px)',
                            transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                        }}
                    >
                        {headerContent && <div className="mb-4">{headerContent}</div>}

                        <div
                            className="flex flex-col items-center w-full"
                            style={{
                                gap: `${Math.max(10, Math.min(14, 50 / childCount))}px`,
                            }}
                        >
                            {children}
                        </div>
                    </div>
                </div>

                {/* Bottom roll */}
                <div
                    className="relative z-30 mx-auto transition-opacity duration-300"
                    style={{
                        width: `${scrollWidth}px`,
                        height: `${bottomHeight}px`,
                        marginTop: '-10px',
                        opacity: parchmentHeight > 30 ? 1 : 0,
                    }}
                >
                    <img
                        src={scrollBottom}
                        alt=""
                        className="w-full h-full object-cover object-top"
                        style={{ display: 'block' }}
                    />
                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0) rotate(-1.5deg); }
                    50% { transform: translateY(-10px) rotate(-0.5deg); }
                }
                .animate-float { animation: float 3s ease-in-out infinite; }

                @keyframes pulse-glow {
                    0%, 100% { opacity: 0.6; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.05); }
                }
                .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
            `}</style>
        </div>
    );
};

export const ScrollButton = ({
    children,
    onClick,
    disabled = false,
    variant = 'default',
    loading = false,
    as: Component = 'button',
    to,
    ...props
}) => {
    const colors = {
        default: 'text-amber-900 hover:text-amber-700',
        primary: 'text-amber-900 hover:text-amber-700',
        accent: 'text-gaia-primary hover:text-gaia-primary',
        success: 'text-gaia-success hover:text-gaia-success',
    };

    const isDisabled = disabled || loading;

    const className = `
        w-full max-w-[280px] py-1 px-4
        transition-all duration-200
        ${colors[variant] || colors.default}
        ${isDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer hover:scale-105 active:scale-95'}
        text-xl md:text-2xl text-center
    `;

    const content = (
        <span style={{
            fontFamily: 'var(--ft-font-tavern)',
            textShadow: '0 1px 1px rgba(255,255,255,0.3)',
        }}>
            {loading ? 'Starting...' : children}
        </span>
    );

    // For links, use pointer-events-none + tabIndex to fully disable navigation
    if (Component === 'a' || to) {
        return (
            <Component
                to={to}
                className={className}
                tabIndex={isDisabled ? -1 : undefined}
                aria-disabled={isDisabled}
                {...props}
            >
                {content}
            </Component>
        );
    }

    return (
        <button onClick={onClick} disabled={disabled || loading} className={className} {...props}>
            {content}
        </button>
    );
};

export default ScrollPanel;
